import { t } from "i18next";
import { useEffect, useState } from "react";

import _ from "lodash";
import { useFetch } from "../../../../hooks/useFetch";
import { Change, StaffChange } from "../../../../types/types";
import { Separator } from "../../../../ui/Separator";
import ChangesTable from "./table/ChangesTable";
import { ColumnDef } from "@tanstack/react-table";
import { ChangeLogsTableData } from "../../../../types/formDataTypes";
import { SimpleDataTable } from "../../../../ui/DataTable/SimpleDataTable";
import { DateTime } from "luxon";
import { Button } from "../../../../ui/Button";
import { TbRestore, TbTrash } from "react-icons/tb";
import DeleteChange from "./DeleteChange";

type Props = {
  id: string | undefined;
};

// Получение изменения по его типу
function getActionByType(change: any) {
  switch (change.type) {
    case "personnel_division_changed":
      return change.division_name;
    case "personnel_position_changed":
      return change.post_name;
    case "personnel_status_changed":
      return t(change.status);
    case "sites_personnel_changes_trigger_rfid_ins":
    case "sites_personnel_changes_trigger_rfid_switches":
    case "sites_personnel_changes_trigger_rfid_out":
    case "sites_personnel_changes_trigger_value_less_thans":
    case "sites_personnel_changes_trigger_value_less_thans":
      return t("trigger_in_action");
    default:
      return change.type;
  }
}

const StaffChangesTab = ({ id }: Props) => {
  const [changes, setChanges] = useState<StaffChange[]>();
  const [{ response, isLoading, error }, doFetchChanges] = useFetch(`/directories/personnels/${id}/changes`);
  let [isDeleteOpen, setIsDeleteOpen] = useState(false);
  let [currentItemId, setCurrentItemId] = useState("");

  useEffect(() => {
    doFetchChanges();
  }, []);

  useEffect(() => {
    setChanges(response?.data as unknown as StaffChange[]);
  }, [response]);

  function openDeleteModal() {
    setIsDeleteOpen(true);
  }

  function closeDeleteModal() {
    setIsDeleteOpen(false);
    setCurrentItemId("");
  }

  function onItemDelete(id: string): void {
    setCurrentItemId(id);
    openDeleteModal();
  }

  function updateChanges() {
    doFetchChanges();
  }

  let nodes = changes?.map((changes) => {
    return {
      id: changes.id,
      type: changes.type,
      at: changes.attributes.at,
      post: changes.attributes.post_name,
      division_name: changes.attributes.division_name,
      status: changes.attributes.status,
      leader: changes.attributes.leader
    };
  });

  nodes = _.sortBy(nodes, []);
  let data = { nodes };

  const columns: ColumnDef<any>[] = [
    {
      id: 'id',
      header: `${t("type")}`,
      size: 1000,
      cell: (props) => t(`${props.row.original.type}`),
    },

    {
      accessorKey: "action",
      header: t("event_result"),
      size: 200,
      minSize: 200,
      maxSize: 200,
      cell: (props) => getActionByType(props.row.original),
    },
    {

      header: t("date"),
      size: 150,
      minSize: 150,
      maxSize: 150,
      cell: (props) => (
        <div className="max-w-[420px]">
          <div className="truncate ">{DateTime.fromISO(props.row.original.at!).toFormat("dd.MM.yyyy")}</div>
          <div className="truncate ">{DateTime.fromISO(props.row.original.at!).toFormat("HH:mm.ss")}</div>
        </div>
      ),
    },
    {
      header: t('actions'),
      size: 150,
      minSize: 150,
      maxSize: 150,
      cell: (props) => (
        <Button
          type="button"
          size={"xs"}
          onClick={() => onItemDelete(props.row.original.id)}
          variant={"outline"}
          className="flex items-center justify-center h-7 p-1 space-x-2"
        >
          <TbRestore />
          <span>{t('return_changes')}</span>
        </Button>
      ),
    },
  ];

  return (
    <div className="relative flex flex-col w-full h-full overflow-hidden bg-background">
      <div className="flex flex-none w-full h-24 text-base gap-2 items-center justify-between px-6">
        <div className="flex flex-col space-y-1">
          <span className="text-2xl font-semibold">{t('personnel_changelog')}</span>
        </div>
      </div>
      {!nodes?.length ? ( // Заглушка для отсутствия данных при поиске
        <div className="flex w-full h-full justify-center items-center">
            {t("no_data")}
        </div>
        ) : (
          <>
            <DeleteChange
              changeId={currentItemId}
              personId={id!}
              isDeleteOpen={isDeleteOpen}
              closeModal={closeDeleteModal}
              updateList={updateChanges}
            />
            <SimpleDataTable data={nodes} columns={columns}/>
          </>
        )}
    </div>
  );
};

export default StaffChangesTab;
